import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';

const breadcrumbItems = [
  { label: '首页', path: '/' },
  { label: '关于我们' },
];

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于 Galgame资源网站PC',
  description: 'Galgame资源网站PC致力于为PC玩家提供最全面的Galgame游戏资源下载服务，涵盖汉化版、日文原版、补丁包及攻略合集。',
};

const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Galgame资源网站PC',
  url: 'https://galgame-pc.com',
  description: '最全PC版Galgame下载平台',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setSubmitting(false);
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qobpsnlsngrm4lm2u0', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试。');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试。');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 — Galgame资源网站PC"
        description="Galgame资源网站PC致力于为PC玩家提供最全面的Galgame游戏资源下载服务。了解我们的品牌故事、口碑评价与联系方式，加入最活跃的Galgame玩家社区。"
        keywords="galgame资源网站pc关于,galgame下载平台介绍,galgame资源站联系方式"
        canonicalPath="/about"
        jsonLd={[aboutJsonLd, orgJsonLd]}
      />
      <Navbar />
      <main className="pt-16 bg-background-50">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="py-6 md:py-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                关于我们
              </h1>
              <p className="text-foreground-500 text-sm md:text-base">
                了解 Galgame资源网站PC 的故事与使命
              </p>
            </div>
          </div>
        </div>

        <section className="py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="aspect-[21/9] rounded-lg overflow-hidden mb-8 md:mb-10 bg-background-200/50">
                <img
                  src="https://readdy.ai/api/search-image?query=Cozy%20anime%20style%20workspace%20with%20multiple%20monitors%20displaying%20visual%20novel%20games%2C%20warm%20ambient%20lighting%20from%20desk%20lamps%2C%20shelves%20filled%20with%20game%20boxes%2C%20Japanese%20room%20aesthetic%20with%20tatami%20elements%2C%20comfortable%20and%20creative%20atmosphere%2C%20clean%20composition%2C%20soft%20colors&width=1800&height=770&seq=about-banner&orientation=landscape"
                  alt="Galgame资源网站PC - 关于我们"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="mb-10 md:mb-14">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  Galgame资源网站PC 成立于2020年，是国内领先的PC端Galgame游戏资源聚合平台。我们深知Galgame玩家在寻找可靠资源时所面临的困难——资源分散、版本混乱、补丁难寻。因此，我们决心打造一个一站式的Galgame资源下载平台。
                </p>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  三年多来，我们始终秉持「让每一位玩家都能轻松玩到喜欢的Galgame」的初心，从最初的几十款资源发展到如今超过4,620款游戏、涵盖汉化版、日文原版、补丁包、攻略合集等多个类别，累计服务超过百万Galgame爱好者。
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  我们的团队由一群狂热的Galgame玩家组成，每一位成员都对Galgame有着深厚的热爱和理解。从资源的筛选、安全检测到分类整理，每一个环节都倾注了我们的热情和专注。我们相信，只有真正热爱Galgame的人，才能为Galgame玩家提供最好的服务。
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-10 md:mb-14">
                {[
                  { icon: 'ri-gamepad-line', number: '4,620+', label: '游戏资源' },
                  { icon: 'ri-group-line', number: '100万+', label: '服务玩家' },
                  { icon: 'ri-download-cloud-2-line', number: '500万+', label: '累计下载' },
                  { icon: 'ri-shield-check-line', number: '100%', label: '安全检测' },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-background-100 border border-background-200/70 rounded-lg p-5 text-center"
                  >
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className={`${stat.icon} text-lg`}></i>
                    </div>
                    <div className="font-heading text-2xl font-bold text-foreground-950 mb-1">
                      {stat.number}
                    </div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="mb-10 md:mb-14">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  玩家口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                  {[
                    {
                      name: '小明',
                      content: '找了好久的CLANNAD汉化版终于在这个网站找到了，下载速度也很快，安装教程很详细，五星好评！',
                      rating: 5,
                    },
                    {
                      name: 'Galgame爱好者',
                      content: '分类很清晰，汉化版和日文原版分开很方便。攻略合集也特别实用，帮我打通了好几个游戏的全路线。',
                      rating: 5,
                    },
                    {
                      name: 'PC玩家小王',
                      content: '作为PC版Galgame玩家，这个网站真的是宝藏！更新速度很快，新作发布没几天就有资源了，必须收藏。',
                      rating: 5,
                    },
                  ].map((review, i) => (
                    <div key={i} className="bg-background-100 border border-background-200/70 rounded-lg p-5">
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: review.rating }).map((_, j) => (
                          <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                        "{review.content}"
                      </p>
                      <span className="text-xs text-foreground-400 font-medium">— {review.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div id="contact">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  联系我们
                </h2>
                <p className="text-foreground-500 text-sm mb-6">
                  有任何问题或建议？请填写以下表单，我们会尽快回复你。
                </p>

                <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 md:p-8 max-w-2xl">
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    id="contact-form"
                    className="space-y-5"
                  >
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        你的昵称 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入你的昵称"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        电子邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入你的电子邮箱"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        留言内容 <span className="text-accent-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                        placeholder="请输入你想说的话（最多500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>

                    <div className="anti-spam-field">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full sm:w-auto px-8 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          提交中...
                        </span>
                      ) : (
                        <>
                          <i className="ri-send-plane-line mr-2"></i>
                          发送留言
                        </>
                      )}
                    </button>

                    {formStatus === 'success' && (
                      <div className="p-4 rounded-lg bg-accent-100/70 text-accent-800 text-sm">
                        <i className="ri-checkbox-circle-line mr-2"></i>
                        感谢你的留言！我们会尽快回复你。
                      </div>
                    )}

                    {formStatus === 'error' && (
                      <div className="p-4 rounded-lg bg-red-50 text-red-700 text-sm">
                        <i className="ri-error-warning-line mr-2"></i>
                        {formError}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}